<template>
  <div>
    <EkitabuDialog
      v-model="dialog"
      max-width="420px"
      content-class="empty-cart-dialog__content"
    >
      <template #activator="{ on }">
        <button
          class="empty-cart-trigger"
          :disabled="isDisabled"
          v-on="on"
        >
          {{ $t("checkout.clearCart") }}
        </button>
      </template>
      <div class="empty-cart-dialog">
        <h3 class="empty-cart-dialog__title">
          {{ $t("checkout.clearCart") }}
        </h3>
        <p class="empty-cart-dialog__body">
          {{ $t("checkout.clearCartConfirmation") }}
        </p>
        <div class="empty-cart-dialog__actions">
          <button
            class="empty-cart-dialog__cancel"
            @click="dialog = false"
          >
            {{ $t("button.cancel") }}
          </button>
          <ApolloMutation
            :mutation="(gql) => removeAllItemsMutation"
            :variables="{
              id: orderId,
            }"
            :update="updateCache"
          >
            <template #default="{ mutate, loading }">
              <button
                class="empty-cart-dialog__confirm"
                :disabled="loading || isDisabled"
                @click="mutate"
              >
                <EkitabuProgressCircular
                  v-if="loading"
                  indeterminate
                  size="14"
                  width="2"
                  color="white"
                  class="mr-1"
                />
                {{ $t("button.accept") }}
              </button>
            </template>
          </ApolloMutation>
        </div>
      </div>
    </EkitabuDialog>
  </div>
</template>

<script>
import OrderQueries from '@/graphql/OrderQueries';

export default {
  name: "EmptyCartButton",
  components: {},
  props: {
    orderId: {
      type: String,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
    };
  },
  computed: {
    removeAllItemsMutation() {
      return OrderQueries.deleteAllOrderLines;
    },
  },
  methods: {
    updateCache(cache, result) {
      // remove orderlines in order from cache
      let data = cache.readQuery({
        query: OrderQueries.getOrder,
        variables: {orderId: this.orderId},
      });

      const newOrderLineEdges = { ...data.order.orderLines, edges: [] };

      data = {
        order: {
          ...data.order,
          orderLines: newOrderLineEdges,
        }
      }

      cache.writeQuery({
        query: OrderQueries.getOrder,
        variables: {orderId: this.orderId},
        data,
      })
      this.dialog = false;
      this.$emit("onRemoved");
    }
  },
};
</script>

<style scoped lang="scss">
.empty-cart-trigger {
  font-family: $book-font-body;
  font-size: 0.75rem;
  font-weight: 700;
  color: $book-grey-dark;
  background: none;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  transition: color 0.2s $book-ease-out;
  text-decoration: none;
  letter-spacing: 0.01em;
}

.empty-cart-trigger:hover {
  color: $book-red;
}

.empty-cart-trigger:disabled {
  color: $book-grey-light;
  cursor: not-allowed;
}

/* Dialog card override */
.empty-cart-dialog {
  background: white;
  border-radius: 24px;
  padding: 30px 28px 24px;
  font-family: $book-font-body;
  box-shadow: 0 24px 60px rgba($book-ink, 0.16);
}

.empty-cart-dialog__title {
  font-family: $book-font-display;
  font-style: italic;
  font-weight: 600;
  font-size: 1.4rem;
  color: $book-ink;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin: 0 0 12px;
}

.empty-cart-dialog__body {
  font-family: $book-font-body;
  font-size: 0.95rem;
  font-weight: 400;
  color: $book-grey-dark;
  line-height: 1.55;
  margin: 0 0 28px;
}

.empty-cart-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.empty-cart-dialog__cancel {
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 700;
  color: $book-grey-dark;
  background: white;
  border: 1px solid $book-grey-lighter;
  border-radius: 12px;
  min-width: 112px;
  min-height: 46px;
  padding: 12px 20px;
  cursor: pointer;
  transition: border-color 0.2s $book-ease-out, background 0.2s $book-ease-out, color 0.2s $book-ease-out;
}

.empty-cart-dialog__cancel:hover {
  border-color: rgba($book-grey, 0.5);
  background: $book-grey-lightest;
}

.empty-cart-dialog__confirm {
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 700;
  color: white;
  background: $book-brand-gradient;
  border: none;
  border-radius: 12px;
  min-width: 124px;
  min-height: 46px;
  padding: 12px 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: $book-brand-shadow;
  transition: transform 0.3s $book-ease-out, box-shadow 0.3s $book-ease-out, opacity 0.2s $book-ease-out;
}

.empty-cart-dialog__confirm:hover {
  box-shadow: $book-brand-shadow;
  transform: translateY(-1px);
}

.empty-cart-dialog__confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Override Vuetify dialog overlay styling */
::v-deep .v-dialog {
  border-radius: 24px !important;
  overflow: hidden;
}

::v-deep .empty-cart-dialog__content {
  box-shadow: none !important;
}

@media (max-width: 599px) {
  .empty-cart-dialog {
    padding: 24px 20px 20px;
  }

  .empty-cart-dialog__title {
    font-size: 1.2rem;
  }

  .empty-cart-dialog__body {
    font-size: 0.875rem;
    margin-bottom: 24px;
  }

  .empty-cart-dialog__cancel,
  .empty-cart-dialog__confirm {
    min-width: 0;
    flex: 1;
  }
}
</style>
